import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AttributionProvider } from './attribution/AttributionContext.jsx';
import { getSessionId } from './analytics.js';

getSessionId(); // establish session ID on first SPA load
import { OfferProvider } from './offers/OfferContext.jsx';
import ScrollToTop from './router/ScrollToTop.jsx';
import HashScroller from './router/HashScroller.jsx';
import RouteAnnouncer from './router/RouteAnnouncer.jsx';
import Home from './routes/Home.jsx';
import About from './routes/About.jsx';
import Mission from './routes/Mission.jsx';
import Methodology from './routes/Methodology.jsx';
import Community from './routes/Community.jsx';
import Forum from './routes/Forum.jsx';
import Contact from './routes/Contact.jsx';
import Pricing from './routes/Pricing.jsx';
import Pay from './routes/Pay.jsx';
import Gift from './routes/Gift.jsx';
import Testimonials from './routes/Testimonials.jsx';
import TeachersOnline from './routes/TeachersOnline.jsx';
import SchoolsWelsh from './routes/SchoolsWelsh.jsx';
import SchoolsLanguages from './routes/SchoolsLanguages.jsx';
import SchoolsLangSoon from './routes/SchoolsLangSoon.jsx';
import SchoolsIrish from './routes/SchoolsIrish.jsx';
import SchoolsSpanish from './routes/SchoolsSpanish.jsx';
import SchoolsFrench from './routes/SchoolsFrench.jsx';
import SchoolsGerman from './routes/SchoolsGerman.jsx';
import SchoolsItalian from './routes/SchoolsItalian.jsx';
import SchoolsEnglish from './routes/SchoolsEnglish.jsx';
import CelebrityCoaching from './routes/CelebrityCoaching.jsx';
import IntensiveLearningHub from './routes/IntensiveLearningHub.jsx';
import IntensiveJapanuary from './routes/IntensiveJapanuary.jsx';
import IntensiveIreland from './routes/IntensiveIreland.jsx';
import IntensiveCroatia from './routes/IntensiveCroatia.jsx';
import IntensiveWelsh5day from './routes/IntensiveWelsh5day.jsx';
import IntensiveWelshTom from './routes/IntensiveWelshTom.jsx';
import IntensiveSpanish4day from './routes/IntensiveSpanish4day.jsx';
import AransNotebook from './routes/AransNotebook.jsx';
import AransNotebookArchive from './routes/AransNotebookArchive.jsx';
import LearnLang from './routes/LearnLang.jsx';
import Alw from './routes/Alw.jsx';
import WelshBedtime from './routes/WelshBedtime.jsx';
import WelshCalonLan from './routes/WelshCalonLan.jsx';
import WelshAnthem from './routes/WelshAnthem.jsx';
import WelshResources from './routes/WelshResources.jsx';
import Privacy from './routes/Privacy.jsx';
import Terms from './routes/Terms.jsx';
import Refunds from './routes/Refunds.jsx';
import NotFound from './routes/NotFound.jsx';
import AppShell from './routes/AppShell.jsx';

// Admin tree is code-split: no public visitor needs the admin JS, so its
// ~600 KB chunk loads on demand only when an /admin/* route is visited.
// Suspense fallback below renders while the chunk fetches (typically <300ms
// on a normal connection); non-lazy public routes don't suspend, so the
// fallback never shows for them.
//
// AdminAuthShell is a layout route wrapping every /admin/* in <AuthProvider>.
// Lazy-loaded too, so the cookie-bridge attempt only fires when someone
// actually navigates into the admin tree.
const AdminAuthShell = lazy(() => import('./admin/AdminAuthShell.jsx'));
const AdminLogin = lazy(() => import('./routes/AdminLogin.jsx'));
const AdminDashboard = lazy(() => import('./routes/AdminDashboard.jsx'));
const AdminSubscribers = lazy(() => import('./routes/AdminSubscribers.jsx'));
const AdminTeachers = lazy(() => import('./routes/AdminTeachers.jsx'));
const AdminEngagement = lazy(() => import('./routes/AdminEngagement.jsx'));
const AdminMarketing = lazy(() => import('./routes/AdminMarketing.jsx'));
const AdminRefunds = lazy(() => import('./routes/AdminRefunds.jsx'));
const AdminSettings = lazy(() => import('./routes/AdminSettings.jsx'));
const AdminCampaignTracking = lazy(() => import('./routes/AdminCampaignTracking.jsx'));
const AdminSubscriptionTracking = lazy(() => import('./routes/AdminSubscriptionTracking.jsx'));

function AdminLoading() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--grey)', fontFamily: 'var(--font-body)', fontSize: 14,
    }}>
      Loading admin…
    </div>
  );
}

export default function App() {
  return (
    <AttributionProvider>
      <OfferProvider>
      <ScrollToTop/>
      <HashScroller/>
      <RouteAnnouncer/>
      <Suspense fallback={<AdminLoading/>}>
        <Routes>
          <Route path="/" element={<Home/>}/>
          <Route path="/about" element={<About/>}/>
          <Route path="/mission" element={<Mission/>}/>
          <Route path="/methodology" element={<Methodology/>}/>
          <Route path="/community" element={<Community/>}/>
          <Route path="/forum" element={<Forum/>}/>
          <Route path="/contact" element={<Contact/>}/>
          <Route path="/pricing" element={<Pricing/>}/>
          <Route path="/pay" element={<Pay/>}/>
          <Route path="/gift" element={<Gift/>}/>
          <Route path="/testimonials" element={<Testimonials/>}/>
          <Route path="/tutors" element={<TeachersOnline/>}/>
          {/* Old path kept as a redirect — may be linked externally. */}
          <Route path="/for-teachers-online" element={<Navigate to="/tutors" replace/>}/>
          <Route path="/schools" element={<Navigate to="/schools1" replace/>}/>
          <Route path="/schools1" element={<SchoolsWelsh/>}/>
          <Route path="/schools2" element={<SchoolsLanguages/>}/>
          <Route path="/schools/irish" element={<SchoolsIrish/>}/>
          <Route path="/schools/spanish" element={<SchoolsSpanish/>}/>
          <Route path="/schools/french" element={<SchoolsFrench/>}/>
          <Route path="/schools/german" element={<SchoolsGerman/>}/>
          <Route path="/schools/italian" element={<SchoolsItalian/>}/>
          <Route path="/schools/english" element={<SchoolsEnglish/>}/>
          {/* Future per-language schools placeholders; unknown slugs redirect */}
          <Route path="/schools/:lang" element={<SchoolsLangSoon/>}/>
          <Route path="/celebrity-coaching" element={<CelebrityCoaching/>}/>
          <Route path="/intensive-learning-hub" element={<IntensiveLearningHub/>}/>
          <Route path="/intensive-japanuary" element={<IntensiveJapanuary/>}/>
          <Route path="/intensive-ireland" element={<IntensiveIreland/>}/>
          <Route path="/intensive-croatia" element={<IntensiveCroatia/>}/>
          <Route path="/intensive-welsh-5day" element={<IntensiveWelsh5day/>}/>
          <Route path="/intensive-welsh-tom" element={<IntensiveWelshTom/>}/>
          <Route path="/intensive-spanish-4day" element={<IntensiveSpanish4day/>}/>
          <Route path="/arans-notebook" element={<AransNotebook/>}/>
          <Route path="/arans-notebook-archive" element={<AransNotebookArchive/>}/>
          <Route path="/learn/:lang" element={<LearnLang/>}/>
          {/* Adult Learners' Week promo lander (Nick's short URL). */}
          <Route path="/alw" element={<Alw/>}/>
          <Route path="/welsh/bedtime" element={<WelshBedtime/>}/>
          <Route path="/welsh/calon-lan" element={<WelshCalonLan/>}/>
          <Route path="/welsh/anthem" element={<WelshAnthem/>}/>
          <Route path="/welsh/resources" element={<WelshResources/>}/>
          <Route path="/privacy" element={<Privacy/>}/>
          <Route path="/terms" element={<Terms/>}/>
          <Route path="/refunds" element={<Refunds/>}/>
          <Route element={<AdminAuthShell/>}>
            <Route path="/admin/login" element={<AdminLogin/>}/>
            <Route path="/admin/dashboard" element={<AdminDashboard/>}/>
            <Route path="/admin/subscribers" element={<AdminSubscribers/>}/>
            <Route path="/admin/teachers" element={<AdminTeachers/>}/>
            <Route path="/admin/engagement" element={<AdminEngagement/>}/>
            <Route path="/admin/marketing" element={<AdminMarketing/>}/>
            <Route path="/admin/refunds" element={<AdminRefunds/>}/>
            <Route path="/admin/settings" element={<AdminSettings/>}/>
            <Route path="/admin/campaigns" element={<AdminCampaignTracking/>}/>
            <Route path="/admin/subscription-tracking" element={<AdminSubscriptionTracking/>}/>
          </Route>
          <Route path="/app" element={<AppShell/>}/>
          <Route path="*" element={<NotFound/>}/>
        </Routes>
      </Suspense>
      </OfferProvider>
    </AttributionProvider>
  );
}
